"use client";

import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CookiePolicy = () => {
  const { t } = useTranslation();
  const lastUpdatedDate = "2026-02-26";

  const Section = ({
    titleKey,
    children,
  }: {
    titleKey: string;
    children: React.ReactNode;
  }) => (
    <section className="mb-8">
      <h2 className="font-display text-lg font-semibold text-foreground mb-3">
        {String(t(titleKey))}
      </h2>
      <div className="text-muted-foreground leading-relaxed space-y-2">
        {children}
      </div>
    </section>
  );

  const P = ({ keyName }: { keyName: string }) => (
    <p>{String(t(keyName))}</p>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        <section className="pt-28 pb-16 md:pt-32 md:pb-20">
          <div className="container mx-auto px-4 md:px-8 max-w-3xl">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
            >
              <ArrowLeft className="h-4 w-4" />
              {String(t("sc.backHome"))}
            </Link>
            <h1 className="font-display text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
              {String(t("cookie.title"))}
            </h1>
            <p className="text-sm text-muted-foreground mb-10">
              {String(t("cookie.lastUpdated"))}: {lastUpdatedDate}
            </p>

            <p className="text-muted-foreground leading-relaxed mb-6">
              {String(t("cookie.intro"))}
            </p>
            <p className="text-muted-foreground leading-relaxed mb-10">
              {String(t("cookie.intro2"))}
            </p>

            <div className="prose prose-sm prose-neutral dark:prose-invert max-w-none text-foreground">
              <Section titleKey="cookie.section1Title">
                <P keyName="cookie.section1P1" />
              </Section>

              <Section titleKey="cookie.section2Title">
                <P keyName="cookie.section2P1" />
                <div className="overflow-x-auto my-4 rounded-lg border border-border">
                  <table className="w-full text-sm text-left">
                    <thead>
                      <tr className="border-b border-border bg-muted/50">
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("cookie.section2TableType"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("cookie.section2TablePurpose"))}
                        </th>
                        <th className="px-4 py-2 font-semibold text-foreground">
                          {String(t("cookie.section2TableRetention"))}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-border last:border-0">
                        <td className="px-4 py-2 text-muted-foreground">
                          {String(t("cookie.section2RowType"))}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {String(t("cookie.section2RowPurpose"))}
                        </td>
                        <td className="px-4 py-2 text-muted-foreground">
                          {String(t("cookie.section2RowRetention"))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <P keyName="cookie.section2P2" />
              </Section>

              <Section titleKey="cookie.section3Title">
                <P keyName="cookie.section3P1" />
                <P keyName="cookie.section3P2" />
              </Section>

              <Section titleKey="cookie.section4Title">
                <P keyName="cookie.section4P1" />
              </Section>

              <Section titleKey="cookie.section5Title">
                <P keyName="cookie.section5P1" />
                <P keyName="cookie.section5P2" />
              </Section>

              <Section titleKey="cookie.section6Title">
                <P keyName="cookie.section6P1" />
                <p className="mt-2">{String(t("cookie.section6Email"))}</p>
              </Section>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicy;
